import React, { useEffect, useState, useRef, useCallback } from "react";
import "./Notifications.css";
import SalonService from "../../services/SalonService";
import { useSalon } from "../../context/SalonContext";
import { FaBell, FaCheckDouble, FaRegEnvelope, FaRegEnvelopeOpen, FaChevronRight, FaRegClock } from "react-icons/fa";
import LoadingIndicator from "../loadingIndicator/LoadingIndicator";

export default function Notifications() {
    const { salonName } = useSalon();
    const customerId = localStorage.getItem("CUSTOMER_ID");
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const observer = useRef();

    useEffect(() => {
        fetchNotifications(page);
        // eslint-disable-next-line
    }, [page]);

    const fetchNotifications = async (pageNumber) => {
        if (!hasMore && pageNumber !== 0) return;

        setLoading(true);
        try {
            const response = await SalonService.getNotifications(customerId, pageNumber, 10);
            const newItems = response.data.content || [];

            if (pageNumber === 0) {
                setNotifications(newItems);
            } else {
                setNotifications((prev) => [...prev, ...newItems]);
            }

            setHasMore(!response.data.last);
        } catch (error) {
            console.error("Error fetching notifications:", error);
        } finally {
            setLoading(false);
        }
    };

    const lastNotificationRef = useCallback(
        (node) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    setPage((prev) => prev + 1);
                }
            });

            if (node) observer.current.observe(node);
        },
        [loading, hasMore]
    );

    const markAsRead = async (id) => {
        try {
            await SalonService.updateNotification(id);
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, read: true } : n))
            );
        } catch (error) {
            console.error("Error updating notification:", error);
        }
    };

    const markAllAsRead = async () => {
        try {
            const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
            if (unreadIds.length === 0) return;

            await Promise.all(unreadIds.map((id) => SalonService.updateNotification(id)));
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        } catch (error) {
            console.error("Error marking all as read:", error);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    return (
        <div className="notifications-page-wrapper">
            <div className="notifications-content-container py-4">
                <div className="notifications-actions-bar">
                    <div className="notifications-count">
                        <FaBell className="me-2 text-primary" />
                        <span>{notifications.filter(n => !n.read).length} Unread</span>
                    </div>
                    {notifications.some((n) => !n.read) && (
                        <button className="premium-mark-all-btn" onClick={markAllAsRead}>
                            <FaCheckDouble className="me-2" /> Mark all as read
                        </button>
                    )}
                </div>

                {notifications.length === 0 && !loading ? (
                    <div className="empty-state-notifications">
                        <div className="empty-icon">🔔</div>
                        <h3>No Notifications</h3>
                        <p>You're all caught up! New updates will appear here.</p>
                    </div>
                ) : (
                    <div className="premium-notifications-list">
                        {notifications.map((n, index) => {
                            const isLast = index === notifications.length - 1;
                            const cardProps = {
                                className: `premium-notification-card ${n.read ? "read" : "unread"}`,
                                onClick: () => markAsRead(n.id),
                                ref: isLast ? lastNotificationRef : null
                            };

                            return (
                                <div key={n.id} {...cardProps}>
                                    <div className="notif-icon-box">
                                        {n.read ? <FaRegEnvelopeOpen className="icon-read" /> : <FaRegEnvelope className="icon-unread" />}
                                    </div>
                                    <div className="notif-content-box">
                                        <div className="notif-header-row">
                                            <h4 className="notif-title">{n.title}</h4>
                                            {!n.read && <span className="unread-dot-premium"></span>}
                                        </div>
                                        <p className="notif-message">{n.message}</p>
                                        <div className="notif-footer-row">
                                            <span className="notif-time">
                                                <FaRegClock className="me-1" /> {formatDate(n.createdAt)}
                                            </span>
                                            <FaChevronRight className="notif-arrow" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {loading && (
                    <div className="notif-loading-indicator">
                        <LoadingIndicator />
                    </div>
                )}

                {!hasMore && notifications.length > 0 && (
                    <p className="end-of-list-text">No more notifications to show</p>
                )}
            </div>
        </div>
    );
}
